import { Combobox } from '@/components/ui';
import { InlineField, type InlineSaveResult } from '@/components/ui/InlineField';
import { contactTitleOptions, useContactTitles } from '@/lib/use-contact-titles';

/*
 * A contact's Title: the Catalogue's "Contact titles" as a pick list, plus a
 * typed title of your own (owner 2026-10-18). The value is always the title
 * STRING — `title` on the contact stays free text — so a stored title that is
 * not on the list displays and saves unchanged.
 */

const EMPTY_LABEL = 'No title';

export interface ContactTitleInputProps {
  id?: string;
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  invalid?: boolean;
  'aria-describedby'?: string;
}

/** The form control (create / edit forms). */
export function ContactTitleInput({
  id,
  value,
  onChange,
  disabled = false,
  invalid = false,
  'aria-describedby': describedBy,
}: ContactTitleInputProps) {
  const { titles, loading } = useContactTitles();
  return (
    <Combobox
      {...(id === undefined ? {} : { id })}
      value={value}
      onChange={onChange}
      options={contactTitleOptions(titles, value)}
      loading={loading}
      freeText
      placeholder={EMPTY_LABEL}
      emptyOptionLabel={EMPTY_LABEL}
      disabled={disabled}
      invalid={invalid}
      {...(describedBy === undefined ? {} : { 'aria-describedby': describedBy })}
    />
  );
}

export interface ContactTitleInlineFieldProps {
  label: string;
  value: string;
  onSave: (next: string) => Promise<InlineSaveResult>;
  disabled?: boolean;
  disabledReason?: string;
  validate?: (next: string) => string | null;
}

/** The inline (click-to-edit) control: contacts manager rows and the contact page. */
export function ContactTitleInlineField({
  label,
  value,
  onSave,
  disabled = false,
  disabledReason,
  validate,
}: ContactTitleInlineFieldProps) {
  // Read-only surfaces render the text and need no list.
  const { titles, loading } = useContactTitles(!disabled);
  return (
    <InlineField
      kind="picker"
      freeText
      label={label}
      value={value}
      onSave={onSave}
      disabled={disabled}
      {...(disabledReason === undefined ? {} : { disabledReason })}
      {...(validate === undefined ? {} : { validate })}
      options={contactTitleOptions(titles, value)}
      optionsLoading={loading}
      emptyOptionLabel={EMPTY_LABEL}
      placeholder={EMPTY_LABEL}
    />
  );
}
